import {
  AloftPrice,
  MODEL_RELIABLE_R_SQUARED_MIN,
  MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX,
  RegressionOutputSuccess,
  SensitivityAnalysisResult,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Flex, ProductColor, Typography, colors } from '@aloft/toolkit-sui';

export type QualityLabel = 'EXCELLENT' | 'SUFFICIENT' | 'INSUFFICIENT' | 'POOR';

export const getThresholdLabel = (
  value: number,
  excellent: number,
  sufficient: number,
  higherIsBetter = true,
): QualityLabel => {
  const passes = higherIsBetter
    ? (v: number, t: number) => v >= t
    : (v: number, t: number) => v <= t;
  if (passes(value, excellent)) return 'EXCELLENT';
  if (passes(value, sufficient)) return 'SUFFICIENT';
  return 'INSUFFICIENT';
};

const qualityColor = (label: QualityLabel): ProductColor => {
  if (label === 'EXCELLENT') return 'textSuccess';
  if (label === 'SUFFICIENT') return 'textPrimary';
  if (label === 'INSUFFICIENT') return 'textWarning';
  return 'textError';
};
type StatTileProps = {
  label: string;
  value: string;
  quality?: QualityLabel;
};

export const StatTile = ({ label, value, quality }: StatTileProps) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="0.5rem"
    padding="1rem"
    backgroundColor="surface1"
    border={`1px solid ${colors.strokeDefault}`}
    borderRadius="8px"
    width="140px"
    height="100px"
    flexShrink={0}
  >
    <Typography variant="body2" color="textLight" textAlign="center">
      {label}
    </Typography>
    <Typography variant="h2">{value}</Typography>
    {quality && (
      <Typography variant="subtitle2" color={qualityColor(quality)}>
        {quality}
      </Typography>
    )}
  </Flex>
);

type StatsRowProps = {
  type: 'regression' | 'sensitivity';
  output?: RegressionOutputSuccess;
  result?: SensitivityAnalysisResult;
};

const getRegressionTiles = (output: RegressionOutputSuccess): StatTileProps[] => [
  {
    label: 'Sample Size',
    value: String(output.sampleSize),
    quality: getThresholdLabel(output.sampleSize, 50, 30),
  },
  { label: 'Variables', value: String(output.numberOfVariables) },
  { label: 'Constant', value: AloftPrice.schema.parse(output.constant).toString(true) },
  {
    label: 'R-squared',
    value: output.modelDiagnostics.rSquared.toFixed(2),
    quality: getThresholdLabel(output.modelDiagnostics.rSquared, 0.7, MODEL_RELIABLE_R_SQUARED_MIN),
  },
  {
    label: 'Standard Error',
    value: output.modelDiagnostics.standardErrorRatio.toFixed(2),
    quality: getThresholdLabel(
      output.modelDiagnostics.standardErrorRatio,
      0.08,
      MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX,
      false,
    ),
  },
];

const getSensitivityTiles = (result: SensitivityAnalysisResult): StatTileProps[] => {
  const optimalRate =
    orUndefined(AloftPrice.schema.parse)(result.selectedRate.rate)?.toString(true) ?? '-';

  const { spreadRatio, reliable } = result.selectedRate;
  const quality: QualityLabel = reliable
    ? getThresholdLabel(spreadRatio, 0.05, 0.1, false)
    : 'POOR';
  return [
    { label: 'Optimal Unit Rate', value: optimalRate },
    {
      label: 'Percent Variance',
      value: `${(spreadRatio * 100).toFixed(2)}%`,
      quality,
    },
  ];
};

export const StatsRow = ({ type, output, result }: StatsRowProps) => {
  const tiles = type === 'regression' ? getRegressionTiles(output!) : getSensitivityTiles(result!);

  return (
    <Flex gap="1.5rem" justifyContent="center" flexWrap="wrap">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </Flex>
  );
};
